import { CirclePlay } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ClientDataModal,
  ClientDuplicationDialog,
  DeleteClientDialog,
  ERROR_OR_INCOMPLETE_STATUS_TAGS,
  LinkClientPartnerDialog,
  SendResultsLinkModal,
  cn,
  fetchClientsForAdvisorByAdvisorIdRequest,
  updateClientsSearchText,
} from '..';
import { PlayIcon } from '../components/PlayIcon';
import {
  selectAdvisorHierarchyState,
  selectClients,
  selectClientsIsLoading,
  selectClientsSearchText,
  selectSessionAdvisor,
} from '../model/selectors';

import { DashboardPageHeader } from '@/components/DashboardPageHeader';
import { DashboardCardView } from '@/components/advisor-mobile-dashboard';
import { useInviteClientTrigger } from '@client/features/invite-client';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { useIsMobile } from '@client/hooks/use-mobile';
import { ColumnDef } from '@tanstack/react-table';
import { Send } from 'lucide-react';
import { createClientTableColumns } from '../components/ClientsTable';
import { DataTable } from '../components/DataTable';
import { Button } from '../components/ui/button';
import { TrialProgress } from '@client/features/trial-mode';
import { useTrialProgressSync } from '@client/hooks/useTrialProgressSync';

type ModalType =
  | 'delete'
  | 'linkPartner'
  | 'sendClientSigninLink'
  | 'clientData'
  | 'newDuplicateClient'
  | 'updateIntakeForm'
  | null;
export interface ModalState {
  type: ModalType;
  client?: Client; // Could be typed more specifically based on your needs
}

const CLIENTS_VIDEO_URL = 'https://www.loom.com/embed/90255565dc404610846adecbd7d82049';

export function AdvisorDashboardClients() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const advisor = useSelector(selectSessionAdvisor);
  const clients = useSelector(selectClients);
  const clientsIsLoading = useSelector(selectClientsIsLoading);
  const { subordinateAdvisors } = useSelector(selectAdvisorHierarchyState);
  const [searchText, setSearchText] = useState(useSelector(selectClientsSearchText));
  const { setInviteClientParams } = useInviteClientTrigger();

  const { isStepCompleted, shouldShowQuickStartGuide } = useTrialProgressSync(
    advisor?.advisorId ?? '',
    advisor?.advisorCreatedDateTime,
  );

  const firstClientWithCompletedIntake = useMemo(
    () =>
      clients.find(
        client => !client.clientTags.some(tag => ERROR_OR_INCOMPLETE_STATUS_TAGS.includes(tag)),
      ),
    [clients],
  );

  const handleReviewResults = useCallback(() => {
    if (firstClientWithCompletedIntake) {
      navigate(`/clients/${firstClientWithCompletedIntake.clientId}/onboarding/results-are-in`);
    }
  }, [firstClientWithCompletedIntake, navigate]);

  // Wait for both clients and subordinateAdvisors to load before rendering the table
  // subordinateAdvisors is null during loading, then becomes an array (even if empty) once loaded
  const isSubordinateAdvisorsLoading = subordinateAdvisors === null;
  const isLoadingData = clientsIsLoading || isSubordinateAdvisorsLoading;

  // Hide the clients table when data is fetched and there are no clients
  const hasNoClients = !isLoadingData && clients.length === 0;

  const handleSearchTextChange = (searchText: string) => {
    setSearchText(searchText);
    dispatch(updateClientsSearchText(searchText));
    localStorage.setItem('clientsSearchText', searchText);
  };

  const [modalState, setModalState] = useState<ModalState>({ type: null });
  const isMobile = useIsMobile();
  const onCloseModal = () => setModalState({ type: null });
  const { flags } = useFeatureFlags();

  // Memoize the columns creation since it depends on clients and setModalState
  const memoizedColumns = useMemo(
    () =>
      createClientTableColumns({
        data: clients,
        setModalState,
        sessionAdvisor: advisor,
        subordinateAdvisors: subordinateAdvisors || ([] as Advisor[]),
        dispatch,
      }),
    [clients, setModalState, advisor, subordinateAdvisors],
  );

  const memoizedHandleRefresh = useCallback(() => {
    dispatch(
      fetchClientsForAdvisorByAdvisorIdRequest({
        advisorId: advisor.advisorId,
        isInvalidation: false,
      }),
    );
  }, [advisor.advisorId, dispatch]);

  // Memoize the entire DataTable instance
  const memoizedDataTable = useMemo(
    () => (
      <DataTable<Client>
        columns={memoizedColumns as ColumnDef<Client>[]}
        getRowId={row => row.clientId}
        data={clients}
        isLoading={isLoadingData}
        globalSearchFilterKey="Client"
        globalSearchText={searchText ?? undefined}
        globalSearchFieldKeys={['clientName', 'email']}
        handleRefresh={memoizedHandleRefresh}
        className="mb-12"
        tableId="clientsTable"
        variant="outlined"
        initialColumnVisibility={{
          organization: Boolean(subordinateAdvisors && subordinateAdvisors.length > 0),
        }}
        initialColumnPinning={{
          left: ['clientName'],
          right: [],
        }}
        handleSearchTextChange={handleSearchTextChange}
        tableSkeletonRows={10}
        initialSorting={[{ id: 'addedDate', desc: true }]}
      />
    ),
    [
      memoizedColumns,
      clients,
      isLoadingData,
      searchText,
      memoizedHandleRefresh,
      subordinateAdvisors,
    ],
  );

  return (
    <div className="h-full">
      {flags.mobileDashboardRedesign ? (
        <main className={cn(isMobile ? 'px-0 pt-2' : 'px-4 pb-12 pt-3 sm:px-6 lg:px-8')}>
          {!isMobile ? (
            <DashboardPageHeader
              title="Clients"
              videoPlayer={
                <PlayIcon
                  id="clients-tab-demo-video"
                  icon={CirclePlay}
                  videoUrl={CLIENTS_VIDEO_URL}
                  size={28}
                />
              }
              actions={
                <Button
                  size="lg"
                  data-testid="invite-client-button-test"
                  onClick={() => setInviteClientParams()}
                  className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
                >
                  <Send className="h-6 w-6" />
                  Invite Clients
                </Button>
              }
            />
          ) : (
            <div className="mb-2 flex items-center justify-between px-4">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-semibold text-gray-900">Clients</h1>
                <PlayIcon
                  id="clients-tab-demo-video"
                  icon={CirclePlay}
                  videoUrl={CLIENTS_VIDEO_URL}
                  size={28}
                />
              </div>
            </div>
          )}

          <div className={isMobile ? '' : 'my-4'}>
            {!isMobile && shouldShowQuickStartGuide && (
              <div className="mb-6">
                <TrialProgress
                  onStartInvite={() => setInviteClientParams()}
                  onReviewResults={handleReviewResults}
                  hasInvitedClient={isStepCompleted('invite-client')}
                  hasCompletedIntakeForm={
                    isStepCompleted('client-intake-form') && Boolean(firstClientWithCompletedIntake)
                  }
                  hasCompletedReview={isStepCompleted('review-results')}
                />
              </div>
            )}
            {!hasNoClients &&
              (isMobile ? (
                <DashboardCardView
                  clients={clients}
                  setModalState={setModalState}
                  sessionAdvisor={advisor}
                  subordinateAdvisors={subordinateAdvisors || []}
                  isLoading={isLoadingData}
                  handleRefresh={memoizedHandleRefresh}
                  globalSearchText={searchText ?? undefined}
                  handleSearchTextChange={handleSearchTextChange}
                />
              ) : (
                memoizedDataTable
              ))}
          </div>
        </main>
      ) : (
        <main className="px-4 pb-12 pt-3 sm:px-6 lg:px-8">
          <DashboardPageHeader
            title="Clients"
            videoPlayer={
              <PlayIcon
                id="clients-tab-demo-video"
                icon={CirclePlay}
                videoUrl={CLIENTS_VIDEO_URL}
                size={28}
              />
            }
            actions={
              <Button
                size="lg"
                data-testid="invite-client-button-test"
                onClick={() => setInviteClientParams()}
                className="bg-primary hover:bg-primary-hover rounded-full py-6 text-base text-white"
              >
                <Send className="h-7 w-7" />
                Add Clients
              </Button>
            }
          />
          {!hasNoClients && memoizedDataTable}
        </main>
      )}
      {modalState.client && (
        <>
          <DeleteClientDialog
            client={modalState.client}
            open={modalState.type === 'delete'}
            onClose={onCloseModal}
          />
          <LinkClientPartnerDialog
            client={modalState.client}
            open={modalState.type === 'linkPartner'}
            onClose={onCloseModal}
          />
          <SendResultsLinkModal
            client={modalState.client}
            open={modalState.type === 'sendClientSigninLink'}
            onClose={onCloseModal}
          />
          <ClientDataModal
            partialClient={modalState.client}
            open={modalState.type === 'clientData'}
            onClose={onCloseModal}
          />
          <ClientDuplicationDialog
            client={modalState.client}
            open={modalState.type === 'newDuplicateClient'}
            onClose={onCloseModal}
          />
        </>
      )}
    </div>
  );
}
